/**
 * Shared GLSL projection helpers. These functions intentionally use hardware
 * mipmaps and continuous axis blending; anti-tiling belongs to a proper
 * overlapping tile blender or to the source texture, not discontinuous cell
 * jumps that create derivative seams.
 */
export const SURFACE_TRIPLANAR_GLSL = /* glsl */ `
#ifndef saturate
#define saturate( a ) clamp( a, 0.0, 1.0 )
#endif
// Rest-space width of one pixel at this fragment. Used to fade detail whose
// wavelength drops below the sampling rate instead of point-sampling it.
float surfacePixelFootprint(vec3 restPos) {
  return max(length(fwidth(restPos)), 1e-6);
}

// 1 → octave fully on (wavelength comfortably > footprint); 0 → sub-pixel, use mean.
float surfaceOctaveFade(float wavelengthMeters, float footprint) {
  return clamp(wavelengthMeters / (footprint * 2.5) - 0.35, 0.0, 1.0);
}

// Sink-side height-derivative budget (per pixel). Legitimate macro slopes stay
// under this; sub-pixel garbage produces large dFdx/dFdy and gets clamped.
const float SURFACE_MAX_HEIGHT_DELTA_PER_PX = 0.03;
// Toksvig-lite: convert clamped-away gradient magnitude into roughness.
const float SURFACE_SPEC_AA_ROUGH_K = 2.0;
// Accumulator for specular AA roughness. Zeroed at fragment start by each
// material (color_fragment); filled by surfaceHeightRestNormal.
float gSurfaceSpecAaRough = 0.0;

vec3 surfaceTriplanarWeights(vec3 n) {
  vec3 w = abs(n);
  w = max(w, vec3(0.0001));
  w = pow(w, vec3(2.2));
  return w / (w.x + w.y + w.z);
}

vec4 surfaceSampleTriplanar(sampler2D tex, vec3 pos, vec3 weights) {
  vec4 sx = texture2D(tex, pos.yz);
  vec4 sy = texture2D(tex, pos.xz);
  vec4 sz = texture2D(tex, pos.xy);
  return sx * weights.x + sy * weights.y + sz * weights.z;
}

vec3 surfaceSampleTriplanarNormal(sampler2D nmap, vec3 pos, vec3 weights, vec3 restNormal) {
  vec3 nx = texture2D(nmap, pos.yz).xyz * 2.0 - 1.0;
  vec3 ny = texture2D(nmap, pos.xz).xyz * 2.0 - 1.0;
  vec3 nz = texture2D(nmap, pos.xy).xyz * 2.0 - 1.0;
  vec3 axisSign = sign(restNormal);
  axisSign = mix(vec3(1.0), axisSign, step(vec3(0.0001), abs(restNormal)));
  nx = vec3(axisSign.x * nx.z, nx.x, axisSign.x * nx.y);
  ny = vec3(ny.x, axisSign.y * ny.z, -axisSign.y * ny.y);
  nz = vec3(nz.x, axisSign.z * nz.y, axisSign.z * nz.z);
  return normalize(nx * weights.x + ny * weights.y + nz * weights.z);
}

vec3 surfaceHeightRestNormal(vec3 restN, vec3 restPos, float height, float strength) {
  vec3 N = normalize(restN);
  float s = max(strength, 0.0);
  if (s < 1e-5) return N;
  vec3 dpdx = dFdx(restPos);
  vec3 dpdy = dFdy(restPos);
  float dhdx = dFdx(height);
  float dhdy = dFdy(height);
  // Clamp per-pixel height deltas so sub-pixel source content cannot produce
  // random normals. Fold the discarded high-frequency variance into roughness.
  float rawGradMag = length(vec2(dhdx, dhdy));
  dhdx = clamp(dhdx, -SURFACE_MAX_HEIGHT_DELTA_PER_PX, SURFACE_MAX_HEIGHT_DELTA_PER_PX);
  dhdy = clamp(dhdy, -SURFACE_MAX_HEIGHT_DELTA_PER_PX, SURFACE_MAX_HEIGHT_DELTA_PER_PX);
  float clampedGradMag = length(vec2(dhdx, dhdy));
  gSurfaceSpecAaRough += saturate((rawGradMag - clampedGradMag) * SURFACE_SPEC_AA_ROUGH_K);
  vec3 r1 = cross(dpdy, N);
  vec3 r2 = cross(N, dpdx);
  float det = dot(dpdx, r1);
  float absDet = max(abs(det), 1e-8);
  vec3 surfGrad = sign(det) * (dhdx * r1 + dhdy * r2);
  return normalize(absDet * N - surfGrad * s);
}
`;
